import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { OpsDeviceKind } from '@m8-app/contracts/internal/ops.js';
import {
  getDevicePlaneCommandSeries,
  getDevicePlaneDevices,
  getDevicePlaneEvents,
  getDevicePlaneOverview,
} from '../api/devicePlane.js';

const KINDS: OpsDeviceKind[] = ['stair', 'ceiling', 'lift', 'unknown'];
const KIND_LABEL: Record<OpsDeviceKind, string> = {
  stair: 'Stair lights',
  ceiling: 'Ceiling lights',
  lift: 'Lifts',
  unknown: 'Uncategorised',
};

const selectCls =
  'bg-surface border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent';

export const TICK = { fontSize: 11, fill: '#898781' };
export const TOOLTIP_STYLE = {
  background: '#1a1a19',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 12,
};

function Kpi({ label, value, sub, tone }: { label: string; value: string | number; sub?: string | undefined; tone?: 'ok' | 'warn' | 'bad' | undefined }) {
  const toneClass =
    tone === 'ok' ? 'text-goodink' : tone === 'warn' ? 'text-warnink' : tone === 'bad' ? 'text-badink' : 'text-ink';
  return (
    <div className="bg-surface border border-white/10 rounded-xl px-4 py-3">
      <p className="text-xs text-muted">{label}</p>
      <p className={`text-2xl font-bold mt-0.5 tabular-nums ${toneClass}`}>{value}</p>
      {sub && <p className="text-xs text-muted mt-0.5">{sub}</p>}
    </div>
  );
}

function Dot({ ok }: { ok: boolean }) {
  return <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${ok ? 'bg-good' : 'bg-bad'}`} />;
}

function presenceDot(p: string) {
  return p === 'online' ? 'bg-good' : p === 'offline' ? 'bg-bad' : 'bg-muted';
}

export function DevicePlaneOverview() {
  const [kindFilter, setKindFilter] = useState('');
  const [presenceFilter, setPresenceFilter] = useState('');
  const [search, setSearch] = useState('');

  const { data: ov } = useQuery({
    queryKey: ['dp-overview'],
    queryFn: getDevicePlaneOverview,
    refetchInterval: 5000,
  });
  const { data: series } = useQuery({
    queryKey: ['dp-series-24h'],
    queryFn: () => getDevicePlaneCommandSeries({ bucket: 'hour' }),
    refetchInterval: 30000,
  });
  const { data: fleet } = useQuery({
    queryKey: ['dp-overview-devices'],
    queryFn: () => getDevicePlaneDevices({ limit: 200 }),
    refetchInterval: 15000,
  });
  const { data: events } = useQuery({
    queryKey: ['dp-events'],
    queryFn: () => getDevicePlaneEvents(30),
    refetchInterval: 5000,
  });

  const points = (series?.points ?? []).map((p) => ({
    t: new Date(p.bucketStart).toLocaleTimeString([], { hour: '2-digit' }),
    total: p.total,
    error: p.error + p.timeout,
  }));

  const okPct = ov && ov.commands24h.total > 0
    ? ((ov.commands24h.ok / ov.commands24h.total) * 100).toFixed(1) + '%'
    : '—';

  const devices = (fleet?.items ?? []).filter((d) =>
    (!kindFilter || d.kind === kindFilter) &&
    (!presenceFilter || d.presence === presenceFilter) &&
    (!search || d.serialNumber.toLowerCase().includes(search.toLowerCase())),
  );
  const groups = KINDS
    .map((k) => ({ kind: k, items: devices.filter((d) => d.kind === k) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-ink">Device Plane — Overview</h1>
        <p className="text-sm text-muted mt-0.5">
          Live view of the middleware (MQTT gateway): fleet presence by category, command flow and pipeline health.
          {ov ? ` Updated ${new Date(ov.generatedAt).toLocaleTimeString()}.` : ''}
        </p>
      </div>

      {/* Fleet + command KPIs (no queue) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Devices total" value={ov?.devices.total ?? '—'}
          sub={ov ? `${ov.devices.byFamily.m8} m8 · ${ov.devices.byFamily['cop-lop']} cop-lop` : undefined} />
        <Kpi label="Online" value={ov?.devices.online ?? '—'} tone="ok" />
        <Kpi label="Offline" value={ov?.devices.offline ?? '—'} tone={ov && ov.devices.offline > 0 ? 'warn' : undefined}
          sub={ov ? `${ov.devices.unknown} unknown` : undefined} />
        <Kpi label="Commands 24 h" value={ov?.commands24h.total ?? '—'} sub={`success ${okPct}`} />
        <Kpi label="Errors" value={ov?.commands24h.error ?? '—'} tone={ov && ov.commands24h.error > 0 ? 'bad' : undefined} />
        <Kpi label="Timeouts" value={ov?.commands24h.timeout ?? '—'} tone={ov && ov.commands24h.timeout > 0 ? 'warn' : undefined} />
      </div>

      {/* Category cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {KINDS.map((k) => {
          const c = ov?.devices.byKind[k];
          return (
            <button
              key={k}
              onClick={() => setKindFilter(kindFilter === k ? '' : k)}
              className={`text-left bg-surface border rounded-xl px-4 py-3 transition-colors ${
                kindFilter === k ? 'border-accent ring-1 ring-accent' : 'border-white/10 hover:border-white/25'
              }`}
            >
              <p className="text-xs text-muted">{KIND_LABEL[k]}</p>
              <p className="text-2xl font-bold tabular-nums mt-0.5 text-ink">{c?.total ?? '—'}</p>
              <p className="text-xs mt-0.5">
                <span className="text-goodink font-medium">{c?.online ?? 0} online</span>
                <span className="text-muted"> · {(c?.total ?? 0) - (c?.online ?? 0)} not online</span>
              </p>
            </button>
          );
        })}
      </div>

      {/* All devices — category-wise chip wall with filters */}
      <div className="bg-surface border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <p className="text-sm font-semibold text-ink2">
            All devices {fleet ? `(${devices.length}${devices.length !== fleet.total ? ` of ${fleet.total}` : ''})` : ''}
          </p>
          <div className="flex gap-2">
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search serial…" className={`${selectCls} w-44 placeholder:text-muted`} />
            <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className={selectCls} aria-label="Category filter">
              <option value="">All categories</option>
              {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
            <select value={presenceFilter} onChange={(e) => setPresenceFilter(e.target.value)} className={selectCls} aria-label="Presence filter">
              <option value="">All presence</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
        </div>
        {groups.length === 0 && (
          <p className="text-sm text-muted py-4 text-center">No devices match the filters.</p>
        )}
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.kind}>
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">
                {KIND_LABEL[g.kind]} <span className="font-normal">({g.items.length})</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {g.items.map((d) => (
                  <Link
                    key={d.deviceId}
                    to={`/device-plane/fleet/${d.deviceId}`}
                    title={`${d.serialNumber} · ${d.presence} · ${d.lifecycleState}`}
                    className="inline-flex items-center gap-1.5 border border-white/10 rounded-lg px-2 py-1 text-xs font-mono text-ink2 hover:border-accent hover:text-ink transition-colors"
                  >
                    <span className={`w-2 h-2 rounded-full ${presenceDot(d.presence)}`} />
                    {d.serialNumber}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Command volume sparkline */}
        <div className="lg:col-span-2 bg-surface border border-white/10 rounded-xl p-4">
          <p className="text-sm font-semibold text-ink2 mb-2">Command volume — last 24 h</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
                <XAxis dataKey="t" tick={TICK} interval="preserveStartEnd" stroke="#383835" />
                <YAxis tick={TICK} allowDecimals={false} stroke="#383835" />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Area type="monotone" dataKey="total" stroke="#3987e5" fill="rgba(57,135,229,0.15)" name="Commands" />
                <Area type="monotone" dataKey="error" stroke="#e66767" fill="rgba(230,103,103,0.15)" name="Failed (error+timeout)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pipeline + gateway health */}
        <div className="space-y-4">
          <div className="bg-surface border border-white/10 rounded-xl p-4">
            <p className="text-sm font-semibold text-ink2 mb-2">Pipeline (Redis Streams)</p>
            {(['deviceEvents', 'registryEvents'] as const).map((key) => {
              const s = ov?.streams[key];
              const label = key === 'deviceEvents' ? 'events:device' : 'events:registry';
              const lag = s?.groups.reduce((acc, g) => acc + (g.lag ?? 0) + g.pending, 0) ?? 0;
              return (
                <div key={key} className="flex items-center justify-between text-sm py-1">
                  <span className="font-mono text-xs text-ink2">{label}</span>
                  <span className="text-xs text-muted tabular-nums">
                    len {s?.length ?? '—'} · backlog {lag}
                    {s && s.dlqLength > 0 && <span className="text-badink font-semibold"> · DLQ {s.dlqLength}</span>}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="bg-surface border border-white/10 rounded-xl p-4">
            <p className="text-sm font-semibold text-ink2 mb-2">Gateway health</p>
            <div className="space-y-1 text-sm text-ink2">
              <p><Dot ok={!!ov?.health.mqttConnected} />MQTT broker {ov?.health.mqttConnected ? 'connected' : 'DISCONNECTED'}</p>
              <p><Dot ok={!!ov?.health.db.ok} />DB {ov?.health.db.latencyMs ?? '—'} ms</p>
              <p><Dot ok={!!ov?.health.redis.ok} />Redis {ov?.health.redis.latencyMs ?? '—'} ms</p>
              <p className="text-xs text-muted">uptime {ov ? Math.floor(ov.health.uptimeSec / 60) : '—'} min</p>
            </div>
          </div>
        </div>
      </div>

      {/* Live event feed */}
      <div className="bg-surface border border-white/10 rounded-xl p-4">
        <p className="text-sm font-semibold text-ink2 mb-2">Live events — events:device stream</p>
        <div className="max-h-72 overflow-y-auto divide-y divide-grid">
          {(events?.items ?? []).map((e) => {
            const p = e.payload as Record<string, unknown>;
            const outcome = typeof p['outcome'] === 'string' ? (p['outcome'] as string) : null;
            const reason = typeof p['reason'] === 'string' ? (p['reason'] as string) : null;
            const errorCode = typeof p['errorCode'] === 'string' ? (p['errorCode'] as string) : null;
            const online = typeof p['online'] === 'boolean' ? (p['online'] as boolean) : null;
            return (
              <div key={e.id} className="flex gap-3 py-1.5 text-xs items-baseline">
                <span className="text-muted tabular-nums whitespace-nowrap">
                  {e.ts ? new Date(e.ts).toLocaleTimeString() : '—'}
                </span>
                <span className="font-mono text-muted whitespace-nowrap">{e.type}</span>
                <span className="text-ink2 truncate">
                  {e.deviceId ? (
                    <Link to={`/device-plane/fleet/${e.deviceId}`} className="text-accent hover:underline font-mono">
                      {e.deviceId.slice(0, 8)}…
                    </Link>
                  ) : null}
                  {typeof p['cmdType'] === 'string' && <span className="font-mono"> {p['cmdType'] as string}</span>}
                  {outcome && (
                    <span className={outcome === 'ok' ? 'text-goodink font-medium' : 'text-badink font-medium'}> → {outcome}</span>
                  )}
                  {errorCode && <span className="text-badink font-mono"> {errorCode}</span>}
                  {reason && <span className="text-warnink"> ({reason})</span>}
                  {online !== null && (
                    <span className={online ? 'text-goodink font-medium' : 'text-badink font-medium'}>
                      {' '}went {online ? 'online' : 'offline'}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
          {events && events.items.length === 0 && (
            <p className="text-sm text-muted py-4 text-center">No events yet — they appear as devices connect and commands settle.</p>
          )}
        </div>
      </div>
    </div>
  );
}
